import React from 'react'
import {
  Button,
  Modal,
  ModalHeader,
  ModalContent,
  ModalActions,
  Text,
  Box,
} from '@lightspeed/design-system-react'
import classNames from 'classnames'

import { StepBreadcrumbs } from '../StepBreadcrumbs'
import { STEPS } from '../types'
import {
  CONTACT_OPTIONS,
  CustomerInfo,
  CUSTOMER_NAME_FIELD,
} from '../../../common/types'
import {
  getDefaultContactOption,
  hasContactInfo,
  isInvalidEmail,
} from '../../../common/customer-contact-utils'
import { ContactNumber } from '../../../common/ContactNumber'
import { FulfilmentType } from '../../../../../../models/fulfilment'
import { TEXT_DELIVERY, TEXT_PICKUP } from './Step2'

interface Props {
  onStepChange: (step: STEPS) => void
  onClose: () => void
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  type: FulfilmentType
  newCustomer: CustomerInfo | null
}

const shouldDisableNext = (customer: CustomerInfo | null) => {
  const name: string[] = []
  let hasAllNeededCustomerInfo = false
  if (customer) {
    Object.values(CUSTOMER_NAME_FIELD).forEach(option => {
      const value = customer[option]
      if (value && value.trim()) {
        name.push(option)
      }
    })
    if (hasContactInfo(customer) && name.length > 0) {
      hasAllNeededCustomerInfo = true
    }
  }

  return !hasAllNeededCustomerInfo || isInvalidEmail(customer)
}

export const Step2Add: React.FC<Props> = ({
  onStepChange,
  onClose,
  onChange,
  type,
  newCustomer,
}) => {
  return (
    <Modal onClose={onClose}>
      <StepBreadcrumbs
        step={STEPS.ADD}
        isAddingCustomer={Boolean(newCustomer)}
      />
      <ModalHeader>
        Contact info for the{' '}
        {type === FulfilmentType.DELIVERY ? TEXT_DELIVERY : TEXT_PICKUP}.
        <Text tag="p" className="vd-mt2">
          Enter details to contact the customer about this&nbsp;order.
        </Text>
      </ModalHeader>
      <ModalContent>
        <>
          <div className="vd-g-row">
            <div className="vd-g-col vd-g-xs-12 vd-g-s-6 vd-pl1 vd-pr1">
              <div className="vd-text-label vd-mb2 vd-mt2">First name</div>
              <div className="vd-value">
                <input
                  className="vd-input"
                  placeholder="Enter first name"
                  value={newCustomer!.firstName || ''}
                  onChange={onChange}
                  name={CUSTOMER_NAME_FIELD.FIRSTNAME}
                />
              </div>
            </div>
            <div className="vd-g-col vd-g-xs-12 vd-g-s-6 vd-pl1 vd-pr1">
              <div className="vd-text-label vd-mb2 vd-mt2">Last name</div>
              <div className="vd-value">
                <input
                  className="vd-input"
                  placeholder="Enter last name"
                  value={newCustomer!.lastName || ''}
                  onChange={onChange}
                  name={CUSTOMER_NAME_FIELD.LASTNAME}
                />
              </div>
            </div>
          </div>
          <div className="vd-g-row vd-mt4">
            <div className="vd-g-col vd-g-xs-12 vd-g-s-6 vd-pl1 vd-pr1">
              <div className="vd-text-label vd-mb2 vd-mt2">Contact number</div>
              <ContactNumber
                customer={newCustomer}
                onChange={onChange}
                defaultOption={getDefaultContactOption(newCustomer)}
              />
            </div>
            <div className="vd-g-col vd-g-xs-12 vd-g-s-6 vd-pl1 vd-pr1">
              <div className="vd-text-label vd-mb2 vd-mt2">Email</div>
              <div className="vd-value">
                <input
                  className={classNames('vd-input', {
                    'vd-input--error': isInvalidEmail(newCustomer),
                  })}
                  placeholder="Enter email"
                  value={newCustomer!.email || ''}
                  onChange={onChange}
                  name={CONTACT_OPTIONS.EMAIL}
                />
              </div>
            </div>
          </div>
        </>
      </ModalContent>
      <ModalActions>
        <Box className="vd-btn-group">
          <Button
            onClick={() => onStepChange(STEPS.CUSTOMER)}
            variant="supplementary"
          >
            Back
          </Button>
          <Button
            onClick={() => onStepChange(STEPS.DETAILS)}
            disabled={shouldDisableNext(newCustomer)}
            variant="go"
          >
            Next
          </Button>
        </Box>
      </ModalActions>
    </Modal>
  )
}

Step2Add.displayName = 'Step2Add'
